'use client';

import { getToolAuthToken } from '@/lib/browser-tool-usage';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';

type OutputFormat = 'png' | 'jpeg' | 'webp';
type BackgroundPreset = 'white' | 'light' | 'warm' | 'charcoal';
type PaddingPresetId = 'tight' | 'balanced' | 'airy';
type BeforeAfterLabelPresetId = 'corner-badge' | 'top-strip' | 'footer-chip';
type BeforeAfterLabelScale = 0.88 | 1 | 1.16;
type LayoutPresetId =
  | 'before-after'
  | 'two-up'
  | 'three-grid'
  | 'four-grid'
  | 'five-showcase'
  | 'six-grid';

type ListingCollageMakerVariant = {
  defaultListingCollageLayoutId?: LayoutPresetId;
  defaultListingCollagePaddingPresetId?: PaddingPresetId;
  defaultListingCollageBackgroundPreset?: BackgroundPreset;
  defaultListingCollageBeforeAfterLabelPresetId?: BeforeAfterLabelPresetId;
  defaultListingCollageBeforeAfterLabelScale?: BeforeAfterLabelScale;
  badgeLabel?: string;
  toolSlug?: string;
  // The generic /tools/photo-collage-maker page reuses this engine; its widget
  // heading must not talk about listings.
  heading?: string;
  subheading?: string;
};

type LoadedImageAsset = {
  id: string;
  previewUrl: string;
  element: HTMLImageElement;
  name: string;
  type: string;
  size: number;
  width: number;
  height: number;
};

type SlotState = {
  imageId: string | null;
  zoom: number;
  offsetX: number;
  offsetY: number;
};

type LayoutRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type LayoutPreset = {
  id: LayoutPresetId;
  label: string;
  summary: string;
  width: number;
  height: number;
  rects: LayoutRect[];
  type?: 'before-after';
};

type PaddingPreset = {
  id: PaddingPresetId;
  label: string;
  value: number;
};

type BeforeAfterLabelPreset = {
  id: BeforeAfterLabelPresetId;
  label: string;
  summary: string;
};

const maxImages = 6;
const maxFileSize = 12 * 1024 * 1024;
const supportedTypes = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/bmp']);

const layoutPresets: LayoutPreset[] = [
  {
    id: 'before-after',
    label: 'Before / after',
    summary: 'A clean side-by-side comparison for secondary marketplace images.',
    width: 1600,
    height: 1200,
    type: 'before-after',
    rects: [
      { x: 0, y: 0, width: 0.49, height: 1 },
      { x: 0.51, y: 0, width: 0.49, height: 1 },
    ],
  },
  {
    id: 'two-up',
    label: 'Two-up',
    summary: 'Two equal image panels for quick comparison, variants, or bundle views.',
    width: 1600,
    height: 1200,
    rects: [
      { x: 0, y: 0, width: 0.49, height: 1 },
      { x: 0.51, y: 0, width: 0.49, height: 1 },
    ],
  },
  {
    id: 'three-grid',
    label: 'Three-grid',
    summary: 'Feature image plus two supporting panels for detail and context.',
    width: 1600,
    height: 1200,
    rects: [
      { x: 0, y: 0, width: 0.56, height: 1 },
      { x: 0.58, y: 0, width: 0.42, height: 0.49 },
      { x: 0.58, y: 0.51, width: 0.42, height: 0.49 },
    ],
  },
  {
    id: 'four-grid',
    label: 'Four-grid',
    summary: 'Balanced 2 × 2 collage for Etsy collages, Amazon secondary images, and detail sets.',
    width: 1600,
    height: 1600,
    rects: [
      { x: 0, y: 0, width: 0.49, height: 0.49 },
      { x: 0.51, y: 0, width: 0.49, height: 0.49 },
      { x: 0, y: 0.51, width: 0.49, height: 0.49 },
      { x: 0.51, y: 0.51, width: 0.49, height: 0.49 },
    ],
  },
  {
    id: 'five-showcase',
    label: 'Five-showcase',
    summary: 'Large hero panel with four supporting images for richer PDP and gallery use.',
    width: 1600,
    height: 1600,
    rects: [
      { x: 0, y: 0, width: 0.58, height: 1 },
      { x: 0.6, y: 0, width: 0.19, height: 0.49 },
      { x: 0.81, y: 0, width: 0.19, height: 0.49 },
      { x: 0.6, y: 0.51, width: 0.19, height: 0.49 },
      { x: 0.81, y: 0.51, width: 0.19, height: 0.49 },
    ],
  },
  {
    id: 'six-grid',
    label: 'Six-grid',
    summary: 'Dense 3 × 2 collage for feature stacks, detail callouts, and marketplace galleries.',
    width: 1600,
    height: 1200,
    rects: [
      { x: 0, y: 0, width: 0.32, height: 0.49 },
      { x: 0.34, y: 0, width: 0.32, height: 0.49 },
      { x: 0.68, y: 0, width: 0.32, height: 0.49 },
      { x: 0, y: 0.51, width: 0.32, height: 0.49 },
      { x: 0.34, y: 0.51, width: 0.32, height: 0.49 },
      { x: 0.68, y: 0.51, width: 0.32, height: 0.49 },
    ],
  },
];

const backgroundPresets: Array<{
  value: BackgroundPreset;
  label: string;
  color: string;
}> = [
  { value: 'white', label: 'White', color: '#ffffff' },
  { value: 'light', label: 'Soft gray', color: '#eef1f5' },
  { value: 'warm', label: 'Warm ivory', color: '#f5efe5' },
  { value: 'charcoal', label: 'Charcoal', color: '#151a23' },
];

const paddingPresets: PaddingPreset[] = [
  { id: 'tight', label: 'Tight', value: 28 },
  { id: 'balanced', label: 'Balanced', value: 52 },
  { id: 'airy', label: 'Airy', value: 84 },
];

const beforeAfterLabelPresets: BeforeAfterLabelPreset[] = [
  { id: 'corner-badge', label: 'Corner badge', summary: 'Compact top-left labels.' },
  { id: 'top-strip', label: 'Top strip', summary: 'Centered tabs at the top of each panel.' },
  { id: 'footer-chip', label: 'Footer chip', summary: 'Soft labels anchored near the bottom.' },
];

const outputOptions: Array<{
  value: OutputFormat;
  label: string;
  mimeType: string;
  extension: string;
  qualityEnabled: boolean;
}> = [
  { value: 'png', label: 'PNG', mimeType: 'image/png', extension: 'png', qualityEnabled: false },
  { value: 'jpeg', label: 'JPG', mimeType: 'image/jpeg', extension: 'jpg', qualityEnabled: true },
  { value: 'webp', label: 'WebP', mimeType: 'image/webp', extension: 'webp', qualityEnabled: true },
];

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function getBaseName(fileName: string) {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
}

function normalizeSourceLabel(type: string | null) {
  if (!type) {
    return 'Auto';
  }

  const label = type.replace('image/', '').toUpperCase();
  return label === 'JPEG' ? 'JPG' : label;
}

function createId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `collage-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getContainSize(width: number, height: number, maxWidth: number, maxHeight: number) {
  const scale = Math.min(maxWidth / width, maxHeight / height, 1);

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

async function loadImageFromUrl(objectUrl: string) {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const nextImage = new Image();
    nextImage.onload = () => resolve(nextImage);
    nextImage.onerror = () =>
      reject(new Error('This image format is not supported in your browser yet.'));
    nextImage.src = objectUrl;
  });

  return image;
}

function triggerDownload(url: string, name: string) {
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function getLayoutById(layoutId: LayoutPresetId) {
  return layoutPresets.find((layout) => layout.id === layoutId) ?? layoutPresets[2];
}

function getPaddingPresetById(paddingId: PaddingPresetId) {
  return paddingPresets.find((preset) => preset.id === paddingId) ?? paddingPresets[1];
}

function getBackgroundByValue(value: BackgroundPreset) {
  return backgroundPresets.find((preset) => preset.value === value) ?? backgroundPresets[0];
}

function getBeforeAfterLabelPresetById(value: BeforeAfterLabelPresetId) {
  return (
    beforeAfterLabelPresets.find((preset) => preset.id === value) ?? beforeAfterLabelPresets[0]
  );
}

function getNextBeforeAfterLabelScale(
  current: BeforeAfterLabelScale,
  direction: 'down' | 'up',
): BeforeAfterLabelScale {
  const scales: BeforeAfterLabelScale[] = [0.88, 1, 1.16];
  const currentIndex = scales.indexOf(current);
  const nextIndex =
    direction === 'up'
      ? clamp(currentIndex + 1, 0, scales.length - 1)
      : clamp(currentIndex - 1, 0, scales.length - 1);

  return scales[nextIndex];
}

function getBeforeAfterLabelScaleLabel(value: BeforeAfterLabelScale) {
  if (value <= 0.88) {
    return 'Small';
  }

  if (value >= 1.16) {
    return 'Large';
  }

  return 'Default';
}

function getPixelRects(layout: LayoutPreset, padding: number) {
  const contentWidth = layout.width - padding * 2;
  const contentHeight = layout.height - padding * 2;

  return layout.rects.map((rect) => ({
    x: padding + rect.x * contentWidth,
    y: padding + rect.y * contentHeight,
    width: rect.width * contentWidth,
    height: rect.height * contentHeight,
  }));
}

function getPlacementMetrics(
  imageWidth: number,
  imageHeight: number,
  slotWidth: number,
  slotHeight: number,
  zoom: number,
) {
  const scaleBase = Math.max(slotWidth / imageWidth, slotHeight / imageHeight);
  const scale = scaleBase * zoom;
  const renderedWidth = imageWidth * scale;
  const renderedHeight = imageHeight * scale;
  const baseX = (slotWidth - renderedWidth) / 2;
  const baseY = (slotHeight - renderedHeight) / 2;
  const minX = renderedWidth > slotWidth ? slotWidth - renderedWidth : 0;
  const maxX = renderedWidth > slotWidth ? 0 : slotWidth - renderedWidth;
  const minY = renderedHeight > slotHeight ? slotHeight - renderedHeight : 0;
  const maxY = renderedHeight > slotHeight ? 0 : slotHeight - renderedHeight;

  return {
    renderedWidth,
    renderedHeight,
    baseX,
    baseY,
    minX,
    maxX,
    minY,
    maxY,
  };
}

function clampOffsets(
  imageWidth: number,
  imageHeight: number,
  slotWidth: number,
  slotHeight: number,
  zoom: number,
  offsetX: number,
  offsetY: number,
) {
  const metrics = getPlacementMetrics(imageWidth, imageHeight, slotWidth, slotHeight, zoom);

  return {
    x: clamp(offsetX, metrics.minX - metrics.baseX, metrics.maxX - metrics.baseX),
    y: clamp(offsetY, metrics.minY - metrics.baseY, metrics.maxY - metrics.baseY),
  };
}

function createSlotStates(slotCount: number, previous: SlotState[] = []) {
  return Array.from(
    { length: slotCount },
    (_, index) =>
      previous[index] ?? {
        imageId: null,
        zoom: 1,
        offsetX: 0,
        offsetY: 0,
      },
  );
}

function autoAssignImages(slotStates: SlotState[], imageIds: string[]) {
  const nextSlots = slotStates.map((slot) => ({ ...slot }));
  let imageCursor = 0;

  for (let index = 0; index < nextSlots.length; index += 1) {
    const currentImageId = nextSlots[index].imageId;

    if (currentImageId && imageIds.includes(currentImageId)) {
      continue;
    }

    while (
      imageCursor < imageIds.length &&
      nextSlots.some((slot) => slot.imageId === imageIds[imageCursor])
    ) {
      imageCursor += 1;
    }

    nextSlots[index].imageId = imageIds[imageCursor] ?? null;
    nextSlots[index].zoom = 1;
    nextSlots[index].offsetX = 0;
    nextSlots[index].offsetY = 0;
    imageCursor += 1;
  }

  return nextSlots;
}

function drawCollageCanvas(
  context: CanvasRenderingContext2D,
  layout: LayoutPreset,
  padding: number,
  backgroundColor: string,
  slotStates: SlotState[],
  images: LoadedImageAsset[],
  beforeLabel: string,
  afterLabel: string,
  beforeAfterLabelPreset: BeforeAfterLabelPresetId,
  beforeAfterLabelScale: BeforeAfterLabelScale,
) {
  context.clearRect(0, 0, layout.width, layout.height);
  context.fillStyle = backgroundColor;
  context.fillRect(0, 0, layout.width, layout.height);

  const slotRects = getPixelRects(layout, padding);
  const isDarkBackground = backgroundColor === '#151a23';
  const ink = isDarkBackground ? '#f8fafc' : '#121826';

  slotRects.forEach((slotRect, index) => {
    const slotState = slotStates[index];
    const image = images.find((candidate) => candidate.id === slotState?.imageId) ?? null;

    context.save();
    context.beginPath();
    context.roundRect(slotRect.x, slotRect.y, slotRect.width, slotRect.height, 28);
    context.clip();

    if (image && slotState) {
      const metrics = getPlacementMetrics(
        image.width,
        image.height,
        slotRect.width,
        slotRect.height,
        slotState.zoom,
      );
      const offsets = clampOffsets(
        image.width,
        image.height,
        slotRect.width,
        slotRect.height,
        slotState.zoom,
        slotState.offsetX,
        slotState.offsetY,
      );

      context.drawImage(
        image.element,
        slotRect.x + metrics.baseX + offsets.x,
        slotRect.y + metrics.baseY + offsets.y,
        metrics.renderedWidth,
        metrics.renderedHeight,
      );
    } else {
      context.fillStyle = isDarkBackground ? 'rgba(255, 255, 255, 0.05)' : 'rgba(18, 24, 38, 0.05)';
      context.fillRect(slotRect.x, slotRect.y, slotRect.width, slotRect.height);
    }

    context.restore();
  });

  if (layout.type === 'before-after') {
    context.save();
    [beforeLabel || 'Before', afterLabel || 'After'].forEach((label, index) => {
      const slotRect = slotRects[index];
      if (!slotRect) {
        return;
      }

      const fontSize = 34 * beforeAfterLabelScale;
      const verticalInset = 20 * beforeAfterLabelScale;
      context.font = `700 ${fontSize}px sans-serif`;

      if (beforeAfterLabelPreset === 'top-strip') {
        const badgeWidth = Math.max(
          170 * beforeAfterLabelScale,
          context.measureText(label).width + 72 * beforeAfterLabelScale,
        );
        const badgeX = slotRect.x + slotRect.width / 2 - badgeWidth / 2;
        context.textAlign = 'center';
        context.fillStyle = isDarkBackground
          ? 'rgba(255, 255, 255, 0.16)'
          : 'rgba(8, 13, 24, 0.72)';
        context.beginPath();
        context.roundRect(
          badgeX,
          slotRect.y + verticalInset,
          badgeWidth,
          58 * beforeAfterLabelScale,
          18 * beforeAfterLabelScale,
        );
        context.fill();
        context.fillStyle = '#ffffff';
        context.fillText(
          label,
          badgeX + badgeWidth / 2,
          slotRect.y + verticalInset + 38 * beforeAfterLabelScale,
        );
        return;
      }

      if (beforeAfterLabelPreset === 'footer-chip') {
        const badgeWidth = Math.max(
          154 * beforeAfterLabelScale,
          context.measureText(label).width + 62 * beforeAfterLabelScale,
        );
        const badgeX = slotRect.x + 24 * beforeAfterLabelScale;
        const badgeY = slotRect.y + slotRect.height - 78 * beforeAfterLabelScale;
        context.textAlign = 'left';
        context.fillStyle = isDarkBackground
          ? 'rgba(255, 255, 255, 0.16)'
          : 'rgba(255, 255, 255, 0.86)';
        context.beginPath();
        context.roundRect(
          badgeX,
          badgeY,
          badgeWidth,
          54 * beforeAfterLabelScale,
          18 * beforeAfterLabelScale,
        );
        context.fill();
        context.fillStyle = isDarkBackground ? '#ffffff' : '#121826';
        context.fillText(
          label,
          badgeX + 30 * beforeAfterLabelScale,
          badgeY + 37 * beforeAfterLabelScale,
        );
        return;
      }

      const badgeWidth = Math.max(
        120 * beforeAfterLabelScale,
        context.measureText(label).width + 44 * beforeAfterLabelScale,
      );
      context.textAlign = 'left';
      context.fillStyle = 'rgba(8, 13, 24, 0.78)';
      context.beginPath();
      context.roundRect(
        slotRect.x + 20 * beforeAfterLabelScale,
        slotRect.y + verticalInset,
        badgeWidth,
        54 * beforeAfterLabelScale,
        18 * beforeAfterLabelScale,
      );
      context.fill();
      context.fillStyle = '#ffffff';
      context.fillText(
        label,
        slotRect.x + 42 * beforeAfterLabelScale,
        slotRect.y + verticalInset + 36 * beforeAfterLabelScale,
      );
    });

    context.restore();
  }
}

export default function BrowserListingCollageMaker({
  variant,
}: {
  variant?: ListingCollageMakerVariant;
}) {
  const initialLayoutId = variant?.defaultListingCollageLayoutId ?? 'three-grid';
  const inputRef = useRef<HTMLInputElement | null>(null);
  const slotInputRef = useRef<HTMLInputElement | null>(null);
  const previewFrameRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const imagesRef = useRef<LoadedImageAsset[]>([]);
  const pendingSlotUploadIndexRef = useRef<number | null>(null);
  const dragPointerRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    offsetX: number;
    offsetY: number;
    slotIndex: number;
  } | null>(null);

  const [layoutId, setLayoutId] = useState<LayoutPresetId>(initialLayoutId);
  const [paddingPresetId, setPaddingPresetId] = useState<PaddingPresetId>(
    variant?.defaultListingCollagePaddingPresetId ?? 'balanced',
  );
  const [backgroundPreset, setBackgroundPreset] = useState<BackgroundPreset>(
    variant?.defaultListingCollageBackgroundPreset ?? 'white',
  );
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('png');
  const [quality, setQuality] = useState(0.88);
  const [beforeLabel, setBeforeLabel] = useState('Before');
  const [afterLabel, setAfterLabel] = useState('After');
  const [beforeAfterLabelPresetId, setBeforeAfterLabelPresetId] =
    useState<BeforeAfterLabelPresetId>(
      variant?.defaultListingCollageBeforeAfterLabelPresetId ?? 'corner-badge',
    );
  const [beforeAfterLabelScale, setBeforeAfterLabelScale] = useState<BeforeAfterLabelScale>(
    variant?.defaultListingCollageBeforeAfterLabelScale ?? 1,
  );
  const [images, setImages] = useState<LoadedImageAsset[]>([]);
  const [slotStates, setSlotStates] = useState<SlotState[]>(() =>
    createSlotStates(getLayoutById(initialLayoutId).rects.length),
  );
  const [selectedSlotIndex, setSelectedSlotIndex] = useState(0);
  const [previewFrameSize, setPreviewFrameSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [hoveredSlotIndex, setHoveredSlotIndex] = useState<number | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isPointerDragging, setIsPointerDragging] = useState(false);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);

  const activeLayout = useMemo(() => getLayoutById(layoutId), [layoutId]);
  const activePadding = useMemo(() => getPaddingPresetById(paddingPresetId), [paddingPresetId]);
  const activeBackground = useMemo(
    () => getBackgroundByValue(backgroundPreset),
    [backgroundPreset],
  );
  const activeBeforeAfterLabelPreset = useMemo(
    () => getBeforeAfterLabelPresetById(beforeAfterLabelPresetId),
    [beforeAfterLabelPresetId],
  );
  const selectedOutput = useMemo(
    () => outputOptions.find((option) => option.value === outputFormat) ?? outputOptions[0],
    [outputFormat],
  );
  const previewSize = useMemo(
    () =>
      getContainSize(
        activeLayout.width,
        activeLayout.height,
        Math.max(320, (previewFrameSize?.width ?? 1080) - 40),
        Math.max(280, (previewFrameSize?.height ?? 720) - 40),
      ),
    [activeLayout.height, activeLayout.width, previewFrameSize],
  );
  const slotRects = useMemo(
    () => getPixelRects(activeLayout, activePadding.value),
    [activeLayout, activePadding.value],
  );
  const activeSlot = slotStates[selectedSlotIndex] ?? null;
  const activeImage = images.find((image) => image.id === activeSlot?.imageId) ?? null;
  const placedImageCount = slotStates.filter((slot) => slot.imageId).length;
  const estimatedWeight = useMemo(() => {
    if (!images.length) {
      return 'Will appear after upload';
    }

    const totalInputSize = images.reduce((sum, image) => sum + image.size, 0);

    if (outputFormat === 'png') {
      return `Around ${formatFileSize(Math.max(120_000, Math.round(totalInputSize * 0.62)))}`;
    }

    const ratio = outputFormat === 'webp' ? quality * 0.34 : quality * 0.5;
    return `Around ${formatFileSize(Math.max(80_000, Math.round(totalInputSize * ratio)))}`;
  }, [images, outputFormat, quality]);

  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  useEffect(() => {
    if (!previewFrameRef.current) {
      return;
    }

    const frame = previewFrameRef.current;
    const syncFrameSize = () => {
      setPreviewFrameSize({
        width: frame.clientWidth,
        height: frame.clientHeight,
      });
    };

    syncFrameSize();
    const observer = new ResizeObserver(syncFrameSize);
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    return () => {
      imagesRef.current.forEach((image) => URL.revokeObjectURL(image.previewUrl));
    };
  }, []);

  useEffect(() => {
    setSlotStates((current) => createSlotStates(activeLayout.rects.length, current));
    setSelectedSlotIndex((current) => clamp(current, 0, activeLayout.rects.length - 1));
  }, [activeLayout]);

  useEffect(() => {
    if (!previewCanvasRef.current) {
      return;
    }

    const canvas = previewCanvasRef.current;
    const devicePixelRatio = window.devicePixelRatio || 1;
    canvas.width = Math.round(previewSize.width * devicePixelRatio);
    canvas.height = Math.round(previewSize.height * devicePixelRatio);

    const context = canvas.getContext('2d');

    if (!context) {
      return;
    }

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.scale(devicePixelRatio, devicePixelRatio);
    context.save();
    context.scale(previewSize.width / activeLayout.width, previewSize.height / activeLayout.height);
    drawCollageCanvas(
      context,
      activeLayout,
      activePadding.value,
      activeBackground.color,
      slotStates,
      images,
      beforeLabel,
      afterLabel,
      beforeAfterLabelPresetId,
      beforeAfterLabelScale,
    );
    context.restore();
  }, [
    activeBackground.color,
    activeLayout,
    activePadding.value,
    afterLabel,
    beforeAfterLabelPresetId,
    beforeAfterLabelScale,
    beforeLabel,
    images,
    previewSize.height,
    previewSize.width,
    slotStates,
  ]);

  function resetOutputState() {
    setResultSize(null);
    setCreditMessage(null);
  }

  async function handleFilesSelection(
    fileList: FileList | File[] | null,
    options?: {
      targetSlotIndex?: number;
    },
  ) {
    const nextFiles = Array.from(fileList ?? []);
    const targetSlotIndex = options?.targetSlotIndex ?? null;

    if (!nextFiles.length) {
      return;
    }

    setErrorMessage(null);
    setCreditMessage(null);
    setResultSize(null);
    setIsLoadingImages(true);

    const availableSlots = Math.max(0, maxImages - images.length);

    if (!availableSlots) {
      setIsLoadingImages(false);
      setErrorMessage('This collage already has 6 images. Remove one before adding another.');
      return;
    }

    const candidateFiles =
      targetSlotIndex === null ? nextFiles.slice(0, availableSlots) : nextFiles.slice(0, 1);
    const validFiles = candidateFiles.filter(
      (file) => supportedTypes.has(file.type) && file.size <= maxFileSize,
    );

    if (!validFiles.length) {
      setIsLoadingImages(false);
      setErrorMessage('Choose PNG, JPG, JPEG, WebP, GIF, or BMP images up to 12 MB each.');
      return;
    }

    try {
      const loadedAssets = await Promise.all(
        validFiles.map(async (file) => {
          const previewUrl = URL.createObjectURL(file);

          try {
            const image = await loadImageFromUrl(previewUrl);

            return {
              id: createId(),
              previewUrl,
              element: image,
              name: file.name,
              type: file.type,
              size: file.size,
              width: image.naturalWidth,
              height: image.naturalHeight,
            } satisfies LoadedImageAsset;
          } catch (error) {
            URL.revokeObjectURL(previewUrl);
            throw error;
          }
        }),
      );

      setImages((current) => {
        const merged = [...current, ...loadedAssets].slice(0, maxImages);
        if (targetSlotIndex === null) {
          setSlotStates((slotCurrent) =>
            autoAssignImages(
              slotCurrent,
              merged.map((image) => image.id),
            ),
          );
        } else {
          const uploadedImage = loadedAssets[0] ?? null;

          if (uploadedImage) {
            setSelectedSlotIndex(targetSlotIndex);
            setSlotStates((slotCurrent) =>
              slotCurrent.map((slot, index) =>
                index === targetSlotIndex
                  ? { ...slot, imageId: uploadedImage.id, zoom: 1, offsetX: 0, offsetY: 0 }
                  : slot,
              ),
            );
          }
        }
        return merged;
      });

      if (targetSlotIndex !== null && nextFiles.length > 1) {
        setErrorMessage('Only the first selected image was added to this panel.');
      } else if (nextFiles.length > candidateFiles.length) {
        setErrorMessage('Only the first 6 images are kept in one collage.');
      } else if (validFiles.length !== candidateFiles.length) {
        setErrorMessage('Some files were skipped because the browser could not use them here.');
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'These images could not be prepared in your browser.',
      );
    } finally {
      setIsLoadingImages(false);
    }
  }

  function removeImage(imageId: string) {
    setImages((current) => {
      const removed = current.find((image) => image.id === imageId);

      if (removed) {
        URL.revokeObjectURL(removed.previewUrl);
      }

      const nextImages = current.filter((image) => image.id !== imageId);
      setSlotStates((slotCurrent) =>
        slotCurrent.map((slot) =>
          slot.imageId === imageId
            ? { ...slot, imageId: null, zoom: 1, offsetX: 0, offsetY: 0 }
            : slot,
        ),
      );
      return nextImages;
    });
    resetOutputState();
  }

  function assignImageToSelectedSlot(imageId: string) {
    setSlotStates((current) =>
      current.map((slot, index) =>
        index === selectedSlotIndex ? { ...slot, imageId, zoom: 1, offsetX: 0, offsetY: 0 } : slot,
      ),
    );
    resetOutputState();
  }

  function openSlotUpload(slotIndex: number) {
    if (images.length >= maxImages) {
      setErrorMessage('This collage already has 6 images. Remove one before adding another.');
      return;
    }

    setErrorMessage(null);
    pendingSlotUploadIndexRef.current = slotIndex;
    setSelectedSlotIndex(slotIndex);
    slotInputRef.current?.click();
  }

  function resetSelectedSlotFraming() {
    setSlotStates((current) =>
      current.map((slot, index) =>
        index === selectedSlotIndex ? { ...slot, zoom: 1, offsetX: 0, offsetY: 0 } : slot,
      ),
    );
    resetOutputState();
  }

  function clearSelectedSlot() {
    setSlotStates((current) =>
      current.map((slot, index) =>
        index === selectedSlotIndex
          ? { ...slot, imageId: null, zoom: 1, offsetX: 0, offsetY: 0 }
          : slot,
      ),
    );
    resetOutputState();
  }

  function updateSelectedSlotZoom(nextZoom: number) {
    if (!activeImage || !activeSlot) {
      return;
    }

    const slotRect = slotRects[selectedSlotIndex];
    const zoom = clamp(nextZoom, 0.9, 2.8);
    const offsets = clampOffsets(
      activeImage.width,
      activeImage.height,
      slotRect.width,
      slotRect.height,
      zoom,
      activeSlot.offsetX,
      activeSlot.offsetY,
    );

    setSlotStates((current) =>
      current.map((slot, index) =>
        index === selectedSlotIndex
          ? { ...slot, zoom, offsetX: offsets.x, offsetY: offsets.y }
          : slot,
      ),
    );
    resetOutputState();
  }

  function getCanvasPointFromClient(clientX: number, clientY: number) {
    const rect = stageRef.current?.getBoundingClientRect();

    if (!rect || rect.width <= 0 || rect.height <= 0) {
      return null;
    }

    const localX = clamp(clientX - rect.left, 0, rect.width);
    const localY = clamp(clientY - rect.top, 0, rect.height);

    return {
      x: localX * (activeLayout.width / rect.width),
      y: localY * (activeLayout.height / rect.height),
      rect,
    };
  }

  function getSlotIndexAtPoint(pointX: number, pointY: number) {
    return slotRects.findIndex(
      (rect) =>
        pointX >= rect.x &&
        pointX <= rect.x + rect.width &&
        pointY >= rect.y &&
        pointY <= rect.y + rect.height,
    );
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLCanvasElement>) {
    const point = getCanvasPointFromClient(event.clientX, event.clientY);

    if (!point) {
      return;
    }

    const slotIndex = getSlotIndexAtPoint(point.x, point.y);

    if (slotIndex < 0) {
      return;
    }

    setSelectedSlotIndex(slotIndex);

    const slot = slotStates[slotIndex];
    const image = images.find((candidate) => candidate.id === slot?.imageId) ?? null;

    if (!slot) {
      return;
    }

    if (!image) {
      openSlotUpload(slotIndex);
      return;
    }

    dragPointerRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: slot.offsetX,
      offsetY: slot.offsetY,
      slotIndex,
    };
    setIsPointerDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (!dragPointerRef.current) {
      const point = getCanvasPointFromClient(event.clientX, event.clientY);
      setHoveredSlotIndex(point ? getSlotIndexAtPoint(point.x, point.y) : null);
      return;
    }

    if (!dragPointerRef.current || dragPointerRef.current.pointerId !== event.pointerId) {
      return;
    }

    const pointer = dragPointerRef.current;
    const slot = slotStates[pointer.slotIndex];
    const image = images.find((candidate) => candidate.id === slot?.imageId) ?? null;
    const rect = stageRef.current?.getBoundingClientRect();
    const slotRect = slotRects[pointer.slotIndex];

    if (!slot || !image || !rect || !slotRect) {
      return;
    }

    const scaleX = activeLayout.width / rect.width;
    const scaleY = activeLayout.height / rect.height;
    const offsets = clampOffsets(
      image.width,
      image.height,
      slotRect.width,
      slotRect.height,
      slot.zoom,
      pointer.offsetX + (event.clientX - pointer.startX) * scaleX,
      pointer.offsetY + (event.clientY - pointer.startY) * scaleY,
    );

    setSlotStates((current) =>
      current.map((candidate, index) =>
        index === pointer.slotIndex
          ? { ...candidate, offsetX: offsets.x, offsetY: offsets.y }
          : candidate,
      ),
    );
  }

  function handlePointerUp(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (dragPointerRef.current?.pointerId === event.pointerId) {
      dragPointerRef.current = null;
      setIsPointerDragging(false);
      resetOutputState();
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  async function runExport() {
    if (!images.length) {
      setErrorMessage('Upload at least one image before exporting.');
      return;
    }

    setIsExporting(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setResultSize(null);

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      const idToken = await getToolAuthToken();

      if (idToken) {
        headers.authorization = `Bearer ${idToken}`;
      }

      const usageResponse = await fetch('/api/tools/usage', {
        method: 'POST',
        headers,
        body: JSON.stringify({ toolSlug: variant?.toolSlug ?? 'listing-collage-maker' }),
      });
      const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
        success?: boolean;
        data?: {
          credits?: {
            plan?: 'guest' | 'free' | 'pro';
            remaining?: number | null;
            windowLabel?: string;
          };
        };
        error?: unknown;
      };

      if (!usageResponse.ok || !usagePayload.success) {
        const message =
          typeof usagePayload.error === 'object' &&
          usagePayload.error &&
          'message' in usagePayload.error
            ? String((usagePayload.error as { message?: unknown }).message)
            : typeof usagePayload.error === 'string'
              ? usagePayload.error
              : 'Credits are not available for another export right now.';
        throw new Error(message);
      }

      const canvas = document.createElement('canvas');
      canvas.width = activeLayout.width;
      canvas.height = activeLayout.height;
      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Your browser could not prepare the export.');
      }

      drawCollageCanvas(
        context,
        activeLayout,
        activePadding.value,
        activeBackground.color,
        slotStates,
        images,
        beforeLabel,
        afterLabel,
        beforeAfterLabelPresetId,
        beforeAfterLabelScale,
      );

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(
          (nextBlob) => resolve(nextBlob),
          selectedOutput.mimeType,
          selectedOutput.qualityEnabled ? quality : undefined,
        );
      });

      if (!blob) {
        throw new Error('The collage export could not finish for this image set.');
      }

      const seedName = images[0]?.name ?? 'listing-collage';
      const fileName = `${getBaseName(seedName)}-${activeLayout.id}.${selectedOutput.extension}`;
      const downloadUrl = URL.createObjectURL(blob);
      triggerDownload(downloadUrl, fileName);
      setResultSize(blob.size);
      window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 4000);

      setCreditMessage(
        usagePayload.data?.credits?.plan === 'pro'
          ? 'Local export finished. Metadata stripped on save.'
          : usagePayload.data?.credits?.remaining !== undefined
            ? `Local export finished. Metadata stripped on save. ${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
            : 'Local export finished. Metadata stripped on save.',
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The collage export could not finish.',
      );
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>
            {variant?.heading ??
              'Build listing collages and simple product infographics in the browser'}
          </h2>
          <p>
            {variant?.subheading ??
              'Upload 2 to 6 images, choose a collage layout, add lightweight text or callout blocks, and export one marketplace-ready image locally.'}
          </p>
        </div>
        <span className="tool-live-badge">{variant?.badgeLabel ?? 'Local browser processing'}</span>
      </div>

      <div className="tool-product-prep-steps" aria-label="Workflow steps">
        <div className="tool-product-prep-step">
          <strong>1. Upload</strong>
          <span>Add 2 to 6 images</span>
        </div>
        <div className="tool-product-prep-step">
          <strong>2. Arrange</strong>
          <span>Layout, spacing, overlays</span>
        </div>
        <div className="tool-product-prep-step">
          <strong>3. Export</strong>
          <span>Download one marketplace-ready visual</span>
        </div>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>Upload</strong>
              <span>Drop a small set of product images and keep the whole collage flow local.</span>
            </div>
            <button
              className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
              onClick={() => {
                pendingSlotUploadIndexRef.current = null;
                inputRef.current?.click();
              }}
              onDragEnter={() => setIsDragActive(true)}
              onDragLeave={() => setIsDragActive(false)}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragActive(true);
              }}
              onDrop={(event) => {
                event.preventDefault();
                setIsDragActive(false);
                void handleFilesSelection(event.dataTransfer.files);
              }}
              type="button"
            >
              <div>
                <strong>
                  {isLoadingImages
                    ? 'Preparing images locally…'
                    : images.length
                      ? `${images.length} image${images.length === 1 ? '' : 's'} ready`
                      : 'Upload collage images or drop them here'}
                </strong>
                <span>
                  {images.length
                    ? `${placedImageCount} of ${slotStates.length} panels filled • Up to 6 browser-friendly images`
                    : 'PNG, JPG, JPEG, WebP, GIF, or BMP up to 12 MB each. Useful for Amazon secondary images, Etsy collages, Shopify PDP blocks, and gallery graphics.'}
                </span>
              </div>
            </button>
            <input
              ref={inputRef}
              accept="image/*"
              className="tool-live-file-input"
              multiple
              onChange={(event) => {
                pendingSlotUploadIndexRef.current = null;
                void handleFilesSelection(event.target.files);
                event.currentTarget.value = '';
              }}
              type="file"
            />
            <input
              ref={slotInputRef}
              accept="image/*"
              className="tool-live-file-input"
              onChange={(event) => {
                const targetSlotIndex = pendingSlotUploadIndexRef.current;
                pendingSlotUploadIndexRef.current = null;
                void handleFilesSelection(event.target.files, {
                  targetSlotIndex:
                    targetSlotIndex === null || targetSlotIndex === undefined
                      ? undefined
                      : targetSlotIndex,
                });
                event.currentTarget.value = '';
              }}
              type="file"
            />
            {images.length ? (
              <div className="tool-collage-bank" aria-label="Uploaded images">
                {images.map((image) => {
                  const isAssigned = activeSlot?.imageId === image.id;

                  return (
                    <article
                      className={`tool-collage-thumb ${isAssigned ? 'tool-collage-thumb-active' : ''}`}
                      key={image.id}
                    >
                      <button
                        className="tool-collage-thumb-assign"
                        onClick={() => assignImageToSelectedSlot(image.id)}
                        type="button"
                      >
                        <img alt={image.name} src={image.previewUrl} />
                        <span>{image.name}</span>
                        <small>
                          {normalizeSourceLabel(image.type)} • {image.width} × {image.height}
                        </small>
                      </button>
                      <button
                        aria-label={`Remove ${image.name}`}
                        className="tool-collage-thumb-remove"
                        onClick={() => removeImage(image.id)}
                        type="button"
                      >
                        Remove
                      </button>
                    </article>
                  );
                })}
              </div>
            ) : null}
          </div>

          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>Arrange</strong>
              <span>Choose a layout, fill the panels, then drag to refine framing.</span>
            </div>

            <div className="tool-live-routes" aria-label="Collage layouts">
              {layoutPresets.map((layout) => (
                <button
                  key={layout.id}
                  className={`tool-live-route ${layoutId === layout.id ? 'tool-live-route-active' : ''}`}
                  onClick={() => {
                    setLayoutId(layout.id);
                    resetOutputState();
                  }}
                  type="button"
                >
                  {layout.label}
                </button>
              ))}
            </div>
            <p className="tool-product-prep-preset-note">{activeLayout.summary}</p>

            <div className="tool-live-preview tool-editor-preview tool-collage-shell">
              <div className="tool-image-editor-header">
                <h3>Preview</h3>
                <div className="tool-image-editor-header-actions tool-collage-editor-actions">
                  <button
                    className="button button-ghost"
                    disabled={!activeImage}
                    onClick={resetSelectedSlotFraming}
                    type="button"
                  >
                    Reset slot
                  </button>
                  <button
                    className="button button-ghost"
                    disabled={!activeSlot?.imageId}
                    onClick={clearSelectedSlot}
                    type="button"
                  >
                    Clear slot
                  </button>
                </div>
              </div>
              <div
                ref={previewFrameRef}
                className="tool-live-preview-frame tool-editor-preview-frame tool-collage-frame"
              >
                <div
                  ref={stageRef}
                  className="tool-collage-stage"
                  style={{
                    width: `${previewSize.width}px`,
                    height: `${previewSize.height}px`,
                    aspectRatio: `${previewSize.width} / ${previewSize.height}`,
                  }}
                >
                  <canvas
                    ref={previewCanvasRef}
                    className={`tool-editor-canvas tool-collage-canvas ${isPointerDragging ? 'tool-collage-canvas-dragging' : ''} ${hoveredSlotIndex !== null && !slotStates[hoveredSlotIndex]?.imageId ? 'tool-collage-canvas-slot-target' : ''}`}
                    onPointerCancel={handlePointerUp}
                    onPointerDown={handlePointerDown}
                    onPointerLeave={() => setHoveredSlotIndex(null)}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                  />
                  {slotRects.map((slotRect, index) => (
                    <div
                      className={`tool-collage-slot-overlay ${selectedSlotIndex === index ? 'tool-collage-slot-overlay-active' : ''} ${hoveredSlotIndex === index ? 'tool-collage-slot-overlay-hovered' : ''} ${slotStates[index]?.imageId ? 'tool-collage-slot-overlay-filled' : 'tool-collage-slot-overlay-empty'}`}
                      key={`${slotRect.x}-${slotRect.y}-${index}`}
                      style={{
                        left: `${(slotRect.x / activeLayout.width) * previewSize.width}px`,
                        top: `${(slotRect.y / activeLayout.height) * previewSize.height}px`,
                        width: `${(slotRect.width / activeLayout.width) * previewSize.width}px`,
                        height: `${(slotRect.height / activeLayout.height) * previewSize.height}px`,
                      }}
                    >
                      {slotStates[index]?.imageId ? (
                        <span>{`Panel ${index + 1}`}</span>
                      ) : (
                        <div className="tool-collage-slot-placeholder">
                          <strong>Add image</strong>
                          <small>{`Panel ${index + 1}`}</small>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="tool-collage-editor-note">
              <strong>{`Selected panel ${selectedSlotIndex + 1}`}</strong>
              <span>
                {activeImage
                  ? 'Drag to reposition, or replace it from the image bank below.'
                  : 'Click the empty panel to upload there, or pick one from the image bank.'}
              </span>
            </div>

            <div className="tool-live-controls">
              <label className="field">
                <span>Background</span>
                <select
                  onChange={(event) => {
                    setBackgroundPreset(event.target.value as BackgroundPreset);
                    resetOutputState();
                  }}
                  value={backgroundPreset}
                >
                  {backgroundPresets.map((preset) => (
                    <option key={preset.value} value={preset.value}>
                      {preset.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Padding</span>
                <select
                  onChange={(event) => {
                    setPaddingPresetId(event.target.value as PaddingPresetId);
                    resetOutputState();
                  }}
                  value={paddingPresetId}
                >
                  {paddingPresets.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {preset.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field tool-live-quality">
                <span>Panel zoom</span>
                <input
                  disabled={!activeImage}
                  max="2.8"
                  min="0.9"
                  onChange={(event) => updateSelectedSlotZoom(Number(event.target.value))}
                  step="0.01"
                  type="range"
                  value={activeSlot?.zoom ?? 1}
                />
                <small>
                  {activeImage ? `${(activeSlot?.zoom ?? 1).toFixed(2)}×` : 'Select a filled panel'}
                </small>
              </label>
            </div>

            <div className="tool-product-prep-advanced">
              <button
                className="button button-ghost tool-product-prep-advanced-toggle"
                onClick={() => setIsAdvancedOpen((current) => !current)}
                type="button"
              >
                {isAdvancedOpen ? 'Hide export settings' : 'Export settings'}
              </button>

              {isAdvancedOpen ? (
                <div className="tool-product-prep-advanced-panel">
                  {activeLayout.type === 'before-after' ? (
                    <>
                      <div className="tool-live-controls tool-collage-export-grid">
                        <label className="field">
                          <span>Label style</span>
                          <select
                            onChange={(event) => {
                              setBeforeAfterLabelPresetId(
                                event.target.value as BeforeAfterLabelPresetId,
                              );
                              resetOutputState();
                            }}
                            value={beforeAfterLabelPresetId}
                          >
                            {beforeAfterLabelPresets.map((preset) => (
                              <option key={preset.id} value={preset.id}>
                                {preset.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <div className="field tool-collage-stepper-field">
                          <span>Label size</span>
                          <div
                            className="tool-collage-stepper"
                            aria-label="Before and after label size"
                          >
                            <button
                              className="button button-ghost"
                              disabled={beforeAfterLabelScale <= 0.88}
                              onClick={() => {
                                setBeforeAfterLabelScale((current) =>
                                  getNextBeforeAfterLabelScale(current, 'down'),
                                );
                                resetOutputState();
                              }}
                              type="button"
                            >
                              -
                            </button>
                            <span>{getBeforeAfterLabelScaleLabel(beforeAfterLabelScale)}</span>
                            <button
                              className="button button-ghost"
                              disabled={beforeAfterLabelScale >= 1.16}
                              onClick={() => {
                                setBeforeAfterLabelScale((current) =>
                                  getNextBeforeAfterLabelScale(current, 'up'),
                                );
                                resetOutputState();
                              }}
                              type="button"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                      <p className="tool-collage-export-note">
                        {activeBeforeAfterLabelPreset.summary}
                      </p>
                      <div className="tool-live-controls tool-collage-export-grid">
                        <label className="field">
                          <span>Before label</span>
                          <input
                            maxLength={24}
                            onChange={(event) => {
                              setBeforeLabel(event.target.value);
                              resetOutputState();
                            }}
                            placeholder="Before"
                            type="text"
                            value={beforeLabel}
                          />
                        </label>
                        <label className="field">
                          <span>After label</span>
                          <input
                            maxLength={24}
                            onChange={(event) => {
                              setAfterLabel(event.target.value);
                              resetOutputState();
                            }}
                            placeholder="After"
                            type="text"
                            value={afterLabel}
                          />
                        </label>
                      </div>
                    </>
                  ) : null}

                  <div className="tool-live-controls tool-collage-export-grid">
                    <label className="field">
                      <span>Format</span>
                      <select
                        onChange={(event) => {
                          setOutputFormat(event.target.value as OutputFormat);
                          resetOutputState();
                        }}
                        value={outputFormat}
                      >
                        {outputOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label
                      className={`field tool-live-quality ${selectedOutput.qualityEnabled ? '' : 'tool-live-quality-disabled'}`}
                    >
                      <span>Compression</span>
                      <input
                        disabled={!selectedOutput.qualityEnabled}
                        max="0.98"
                        min="0.45"
                        onChange={(event) => {
                          setQuality(Number(event.target.value));
                          resetOutputState();
                        }}
                        step="0.01"
                        type="range"
                        value={quality}
                      />
                      <small>
                        {selectedOutput.qualityEnabled
                          ? outputFormat === 'webp'
                            ? 'Smaller file, softer detail.'
                            : 'Balanced file size and detail.'
                          : 'PNG keeps full quality.'}
                      </small>
                    </label>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>Export</strong>
              <span>
                Download one marketplace-ready collage or infographic when the framing looks right.
              </span>
            </div>
            <div className="hero-actions tool-live-actions">
              <button
                className="button"
                disabled={!images.length || isLoadingImages || isExporting}
                onClick={() => void runExport()}
                type="button"
              >
                {!images.length
                  ? 'Upload images to export'
                  : isLoadingImages
                    ? 'Preparing images…'
                    : isExporting
                      ? 'Exporting locally…'
                      : `Export as ${selectedOutput.label}`}
              </button>
            </div>
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel tool-product-prep-summary">
          <h3>Summary</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Layout</strong>
              <span>{activeLayout.label}</span>
            </div>
            <div>
              <strong>Canvas</strong>
              <span>{`${activeLayout.width} × ${activeLayout.height}`}</span>
            </div>
            <div>
              <strong>Images</strong>
              <span>{`${placedImageCount} of ${slotStates.length} panels filled`}</span>
            </div>
            <div>
              <strong>Format</strong>
              <span>{selectedOutput.label}</span>
            </div>
            <div>
              <strong>Comparison labels</strong>
              <span>
                {activeLayout.type === 'before-after'
                  ? `${activeBeforeAfterLabelPreset.label} · ${getBeforeAfterLabelScaleLabel(beforeAfterLabelScale)}`
                  : 'Off'}
              </span>
            </div>
            <div>
              <strong>Est. weight</strong>
              <span>{resultSize ? formatFileSize(resultSize) : estimatedWeight}</span>
            </div>
          </div>
          <div className="tool-live-panel-note">
            <strong>Local only</strong>
            <span>
              Upload, layout, preview, and export stay in the browser. The saved file is rebuilt
              from canvas, so common metadata is stripped on save.
            </span>
          </div>
        </aside>
      </div>
    </article>
  );
}
